'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';

import { GetRecommendationsResponse } from 'algoliasearch';
import dynamic from 'next/dynamic';

import AlgoliaProductCarousel from '@/components/Carousel/ProductCarousel/AlgoliaProductCarousel';
import { useShoppingCartContext } from '@/components/Providers/ShoppingCartProvider';
import Container from '@/components/V2/Container';
import { ALGOLIA_CONFIDENCE_SCORE_THRESHOLD, recommendClient } from '@/config/algolia';
import { ALGOLIA_INDEX_NAME } from '@/config/env';
import cn from '@/helpers/utils/classNames';
import { getFreeShippingMessageByCount } from '@/helpers/utils/common';
import { useAppContext } from '@/layouts/AppProvider';
import { AlgoliaProduct } from '@/types/Algolia';

const DontForgetSectionStrapi = dynamic(() => import('./DontForgetSectionStrapi'), { ssr: true });

const FrequentlyBoughtTogetherItemsAlgolia = ({ itemCode: itemCodes }: { itemCode: string[] }) => {
  const { isBackOffice } = useAppContext();
  const { qualifiedItemsCount } = useShoppingCartContext();
  // const [isErrored, setErrored] = useState<boolean>(false);
  const [isFetching, setFetching] = useState<boolean>(true);
  const [isModelReady, setModelReady] = useState<boolean>(true);

  const [recommendations, setRecommendations] = useState<GetRecommendationsResponse['results']>([]);

  const getFrquentlyBoughtItems = useCallback(async () => {
    try {
      if (!itemCodes.length) return setFetching(false);
      const recommendations = await recommendClient.getRecommendations({
        requests: itemCodes.map(itemCode => ({
          indexName: ALGOLIA_INDEX_NAME,
          threshold: ALGOLIA_CONFIDENCE_SCORE_THRESHOLD,
          maxRecommendations: Math.max(Math.ceil(6 / itemCodes.length), 2),
          model: 'bought-together',
          objectID: itemCode,
          queryParameters: {
            ruleContexts: isBackOffice ? ['backoffice', 'cart_page'] : ['replicated_site', 'cart_page'],
          },
        })),
      });
      setRecommendations(recommendations.results);
      setFetching(false);
    } catch (error) {
      const err = error as { status: number; message: string };
      setFetching(false);

      if (err.status == 404) {
        setModelReady(false);
      }
    }
  }, [isBackOffice, isModelReady, itemCodes]);

  useEffect(() => {
    getFrquentlyBoughtItems();
  }, [itemCodes, getFrquentlyBoughtItems]);

  const showFallabck = !isFetching && recommendations.every(r => r.hits.length == 0);

  const productsRaw = useMemo(
    () =>
      recommendations.reduce((prev: AlgoliaProduct[], curr) => {
        const products = curr.hits.map(h => ({ ...h, __autocomplete_queryID: curr.queryID }) as AlgoliaProduct);
        return [...prev, ...products];
      }, []),
    [recommendations]
  );

  const uniqueProducts = useMemo(
    () => Array.from(new Map(productsRaw.map(item => [item.objectID, item])).values()),
    [productsRaw]
  );

  const products = useMemo(
    () =>
      uniqueProducts
        .sort((a, b) => b._score - a._score)
        .sort((a, b) => b.TruckIconFreeShippingQualified?.localeCompare(a.TruckIconFreeShippingQualified || '') || 0),
    [uniqueProducts]
  );

  if (showFallabck) return <DontForgetSectionStrapi />;

  const freeShippingMessage = getFreeShippingMessageByCount(qualifiedItemsCount);
  return (
    <Container className="xl:!v2-px-0 lg:!v2-px-0 v2-pb-[0px] v2-pt-[107px] sm:v2-pt-0 sm:v2-px-4 xs:v2-px-4">
      <div className="v2-my-10 sm:v2-my-[137px]">
        <h3
          className={cn(
            'v2-font-sweet-sans-pro v2-font-normal sm:v2-text-left v2-text-center v2-text-4xl v2-text-neutral-800 v2-mb-4',
            { 'v2-animate-pulse v2-w-7': isFetching }
          )}
        >
          {!isFetching && 'Frequently Bought Together'}
        </h3>
        {!isFetching && (
          <p className="v2-font-sweet-sans-pro v2-font-normal v2-text-base v2-text-[#344054] v2-mb-10 v2-text-center md:v2-text-left">
            {freeShippingMessage}
          </p>
        )}
        <div className="__frequently-bought-together-slider">
          <AlgoliaProductCarousel products={products} isBackOffice={isBackOffice} />
        </div>
      </div>
    </Container>
  );
};

export default FrequentlyBoughtTogetherItemsAlgolia;
